/**
 * Contesto di autenticazione: profilo, perimetro e operazioni di sessione.
 *
 * Il contesto e' separato dal provider per una ragione pratica: React Fast
 * Refresh ricarica in modo pulito un file che esporta solo componenti o solo
 * valori, non un file che mescola le due cose.
 */

import { createContext, useContext } from "react";
import type { IdNodo, UtenteCorrente } from "../api/types";

export interface ValoreAutenticazione {
  /** Utente autenticato, o `null` se la sessione non e' aperta. */
  utente: UtenteCorrente | null;
  /** Vero finche' e' in corso il ripristino della sessione all'avvio. */
  inAvvio: boolean;
  /** Vero se il ruolo e' `super_admin`. */
  eSuperAdmin: boolean;
  /**
   * Nodi su cui l'utente puo' operare.
   *
   * E' il perimetro del redattore; per il super admin l'insieme e' vuoto e
   * significa «nessuna restrizione». La UI lo rispecchia, ma **la decisione
   * autorevole resta dell'API**: l'autorizzazione vive negli endpoint.
   */
  nodiAssegnati: IdNodo[];
  /**
   * Amministratore nei cui panni **non** si e' — cioe' quello che sta operando
   * come l'utente corrente — se la sessione e' un'impersonificazione.
   */
  impersonatoDa: string | null;
  /** Scadenza dell'impersonificazione in corso. */
  impersonificazioneScadeIl: string | null;
  accedi: (nomeUtente: string, password: string) => Promise<void>;
  /** Passa nei panni di un'altra utenza per un'ora. Solo per il super admin. */
  impersonifica: (idUtente: number) => Promise<void>;
  /** Torna alla propria utenza. */
  terminaImpersonificazione: () => void;
  esci: () => void;
}

export const ContestoAutenticazione = createContext<ValoreAutenticazione | null>(null);

/** Accede al contesto di autenticazione. Fallisce se usato fuori dal provider. */
export function useAutenticazione(): ValoreAutenticazione {
  const valore = useContext(ContestoAutenticazione);
  if (!valore) {
    throw new Error("useAutenticazione va usato dentro <FornitoreAutenticazione>.");
  }
  return valore;
}
